import React, { useMemo, useState } from "react";
import { TextFieldVariant, Typeahead } from "@merge-api/merge-javascript-shared";
import PeriodPicker from "./components/PeriodPicker";
import { ConditionPreset } from "../../../../../models/Entities";
import { PERIOD_OPTIONS, PERIOD_OPTIONS_PLURAL_MAP } from "../constants";

type RelativeDatePickerProps = {
  conditionPreset: Partial<ConditionPreset>;
  onConditionPresetChange: (conditionPreset: Partial<ConditionPreset>) => void;
  disabled?: boolean;
};

const RelativeDatePicker = ({
  conditionPreset,
  onConditionPresetChange,
  disabled,
}: RelativeDatePickerProps) => {
  const [value, setValue] = useState(
    conditionPreset.value ? Number(conditionPreset.value) : undefined,
  );
  const [period, setPeriod] = useState<string | null>(
    conditionPreset.period ? conditionPreset.period?.toLowerCase() : null,
  );

  const getOptionLabel = (option: string) => {
    if (value === 1) {
      return PERIOD_OPTIONS_PLURAL_MAP[option] || option;
    }
    return option;
  };

  const options = useMemo(() => {
    return PERIOD_OPTIONS.map((option) => ({
      value: option,
      label: getOptionLabel(option),
    }));
  }, [value]);

  const selectedOption = period
    ? {
        value: period,
        label: getOptionLabel(period),
      }
    : null;

  const handleValueChange = (newValue: number | undefined) => {
    if (disabled) return;
    setValue(newValue);
    if (newValue)
      onConditionPresetChange({
        ...conditionPreset,
        value: newValue.toString(),
      });
  };

  const handlePeriodChange = (newPeriod: string | null) => {
    if (disabled) return;
    if (newPeriod)
      onConditionPresetChange({
        ...conditionPreset,
        period: newPeriod,
      });
    setPeriod(newPeriod);
  };

  return (
    <div className="flex items-center">
      <>
        <PeriodPicker
          value={value ? Number(value) : undefined}
          setValue={handleValueChange}
          increment={() => handleValueChange((value || 0) + 1)}
          decrement={() => handleValueChange(value && value > 1 ? value - 1 : 1)}
          disabled={disabled}
        />
      </>

      <div className="ml-4 mx-1 my-3 w-0 border-l border-gray-10">&nbsp;</div>
      <div className="w-50">
        <Typeahead
          options={options}
          getOptionLabel={(option) => option.label}
          onChange={(_: any, option: { value: string } | null) =>
            handlePeriodChange(option?.value || null)
          }
          value={selectedOption}
          borderVariant={TextFieldVariant.InlinedRounded}
          disableClearable
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default RelativeDatePicker;
